import { useCallback, useEffect, useRef } from 'react';

/**
 * Debounces `fn`. Returns `[schedule, flush]`: `schedule(...args)` runs `fn` after `ms` of quiet,
 * `flush()` runs a pending call now. A pending call is flushed on unmount.
 */
export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, ms: number) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const pending = useRef<{ args: A; timer: ReturnType<typeof setTimeout> } | null>(null);

  const flush = useCallback(() => {
    const p = pending.current;
    if (!p) return;
    clearTimeout(p.timer);
    pending.current = null;
    fnRef.current(...p.args);
  }, []);

  const schedule = useCallback(
    (...args: A) => {
      if (pending.current) clearTimeout(pending.current.timer);
      pending.current = { args, timer: setTimeout(flush, ms) };
    },
    [flush, ms],
  );

  useEffect(() => flush, [flush]);
  return [schedule, flush] as const;
}
